// FCP design system — Surface primitives (v2.8.0)
//
// Card        → bordered surface for bounded objects (records, panels)
// Surface     → unbordered tinted area, used for nested sections
// Badge       → tag/status pill
// Money       → properly-formatted currency with tabular figures
// DateTime    → properly-formatted date/time
// Stat        → label + big number (dashboard metric cards)

import React from 'react';
import './Surface.css';

export function Card({ children, padding = 'md', className = '', as: Tag = 'div', ...rest }) {
  return (
    <Tag
      className={`fcp-card fcp-card--pad-${padding} ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export function Surface({ children, tone = 'sunk', className = '', as: Tag = 'div', ...rest }) {
  return (
    <Tag
      className={`fcp-surface fcp-surface--${tone} ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export function Badge({ children, tone = 'gray', className = '', ...rest }) {
  return (
    <span className={`fcp-badge fcp-badge--${tone} ${className}`} {...rest}>
      {children}
    </span>
  );
}

/**
 * Money — formats a numeric amount as currency with tabular figures.
 * Uses Intl.NumberFormat for locale-aware separators. Defaults USD.
 */
export function Money({ amount, currency = 'USD', locale, className = '' }) {
  const num = Number(amount);
  if (!isFinite(num)) {
    return <span className={`fcp-tabular ${className}`}>—</span>;
  }
  const formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
  return <span className={`fcp-tabular ${className}`}>{formatted}</span>;
}

/**
 * DateTime — renders a date with a sane default short format.
 * `format` can be 'short' (Tue, May 5) | 'long' (Tuesday, May 5, 2026) |
 * 'time' (3:42 PM) | 'datetime' (May 5, 3:42 PM) | 'numeric' (5/5/2026).
 */
export function DateTime({ value, format = 'short', locale, className = '' }) {
  if (!value) return <span className={className}>—</span>;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return <span className={className}>—</span>;

  const opts = {
    short:    { weekday: 'short', month: 'short', day: 'numeric' },
    long:     { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
    time:     { hour: 'numeric', minute: '2-digit' },
    datetime: { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' },
    numeric:  { month: 'numeric', day: 'numeric', year: 'numeric' },
  }[format] || { month: 'short', day: 'numeric' };

  return <span className={`fcp-tabular ${className}`}>{d.toLocaleString(locale, opts)}</span>;
}

/**
 * Stat — small label above a big tabular number. Used in dashboards.
 */
export function Stat({ label, value, hint, tone = 'default', className = '' }) {
  return (
    <div className={`fcp-stat fcp-stat--${tone} ${className}`}>
      <div className="fcp-stat__label">{label}</div>
      <div className="fcp-stat__value fcp-tabular">{value}</div>
      {hint && <div className="fcp-stat__hint">{hint}</div>}
    </div>
  );
}
